import React, { useState, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';

import biService from '../../../service/bi.service';
import { formatCurrency } from '../../../utils/currencyHelper';
import DrillSelectionPath from './DrillSelectionPath';

const KPI_FONT_LIMITS = {
  title: { min: 12, max: 34 },
  value: { min: 16, max: 96 },
  icon: { min: 16, max: 80 },
};

const KPIReport = ({ report, linkedReportIdState, activeLevelState, filterValsState, reportStatesState, height }) => {
  const [chartData, setChartData] = useState({});
  const [chartHeight, setChartHeight] = useState(600);
  const contentRef = useRef(null);
  const valueRowRef = useRef(null);

  const { activeLevel, setActiveLevel } = activeLevelState;
  const { filterVals, setFilterVals } = filterValsState;
  const { linkedReportId, setLinkedReportId } = linkedReportIdState;
  const { reportStates, setReportStates } = reportStatesState;

  const getChartData = useCallback(() => {
    biService.getReportData(report.id, activeLevel, filterVals).then((data) => setChartData(data));
  }, [report.id, activeLevel, filterVals]);

  useEffect(() => {
    if (height) setChartHeight(height);
  }, [height]);

  useEffect(() => {
    const prevReport = reportStates[reportStates.length - 1];
    if (Object.keys(report).length > 0 && prevReport?.reportId !== report.id) {
      getChartData();
    }
  }, [report, getChartData, activeLevel, reportStates]);

  const hasDrillSelections = (filterVals?.filter ?? []).some((item) => !item?.isDefault);
  const compactLayout = chartHeight < 320;
  const formattedValue = formatCurrency(chartData.data);

  useLayoutEffect(() => {
    const content = contentRef.current;
    const valueRow = valueRowRef.current;

    if (!content || !valueRow) return undefined;

    let animationFrameId;

    const setFontScale = (scale) => {
      Object.entries(KPI_FONT_LIMITS).forEach(([name, limits]) => {
        const fontSize = limits.min + (limits.max - limits.min) * scale;
        content.style.setProperty(`--kpi-${name}-font-size`, `${fontSize}px`);
      });
    };

    const fitContent = () => {
      if (!content.clientWidth || !content.clientHeight) return;

      let lowerScale = 0;
      let upperScale = 1;

      for (let index = 0; index < 10; index += 1) {
        const candidateScale = (lowerScale + upperScale) / 2;
        setFontScale(candidateScale);

        const fitsWidth = valueRow.scrollWidth <= content.clientWidth + 1 && content.scrollWidth <= content.clientWidth + 1;
        const contentHeight = Array.from(content.children).reduce((totalHeight, child) => {
          const style = window.getComputedStyle(child);
          return totalHeight + child.getBoundingClientRect().height + parseFloat(style.marginTop) + parseFloat(style.marginBottom);
        }, 0);
        const fitsHeight = contentHeight <= content.clientHeight + 1;

        if (fitsWidth && fitsHeight) {
          lowerScale = candidateScale;
        } else {
          upperScale = candidateScale;
        }
      }

      setFontScale(lowerScale);
    };

    const scheduleFit = () => {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = requestAnimationFrame(fitContent);
    };

    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(scheduleFit);
    resizeObserver?.observe(content);
    Array.from(content.children).forEach((child) => resizeObserver?.observe(child));
    window.addEventListener('resize', scheduleFit);
    scheduleFit();

    return () => {
      cancelAnimationFrame(animationFrameId);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', scheduleFit);
    };
  }, [chartHeight, formattedValue, report.name, filterVals]);

  const handleResetDrill = useCallback(() => {
    setFilterVals((prev) => {
      const defaultFilters = (prev?.filter ?? []).filter((item) => item?.isDefault);
      return { ...prev, filter: defaultFilters };
    });
    setActiveLevel(null);
  }, [setFilterVals, setActiveLevel]);

  const handleSelectDrillLevel = useCallback(
    (targetIndex) => {
      setFilterVals((prev) => {
        const defaultFilters = (prev?.filter ?? []).filter((item) => item?.isDefault);
        const drillFilters = (prev?.filter ?? []).filter((item) => !item?.isDefault);
        const nextDrillFilters = drillFilters.slice(0, targetIndex + 1);
        return { ...prev, filter: [...defaultFilters, ...nextDrillFilters] };
      });
      setActiveLevel((prev) => (prev === null ? null : targetIndex));
    },
    [setFilterVals, setActiveLevel]
  );

  const KPIIcon = ({ value }) => {
    const field = report.biField[0];

    const threshold = {
      min: field.thresholdMin ? parseFloat(field.thresholdMin) : null,
      max: field.thresholdMax ? parseFloat(field.thresholdMax) : null,
      arrow: field.thresholdArrow,
    };

    const type = threshold.min !== null && threshold.max !== null ? 'interval' : threshold.min !== null ? 'from' : 'to';
    const val = parseFloat(value);
    const iconProps = {
      color: '',
      direction: '',
      good: false,
    };

    switch (type) {
      case 'interval':
        if (threshold.min <= val && val <= threshold.max) {
          iconProps.color = 'green';
          iconProps.direction = 'up';
          iconProps.good = true;
        } else {
          iconProps.color = 'red';
          iconProps.direction = 'down';
          iconProps.good = false;
        }
        break;
      case 'from':
        if (threshold.min <= val) {
          iconProps.color = 'green';
          iconProps.direction = 'up';
          iconProps.good = true;
        } else {
          iconProps.color = 'red';
          iconProps.direction = 'down';
          iconProps.good = false;
        }
        break;
      case 'to':
        if (threshold.max >= val) {
          iconProps.color = 'green';
          iconProps.direction = 'down';
          iconProps.good = true;
        } else {
          iconProps.color = 'red';
          iconProps.direction = 'up';
          iconProps.good = false;
        }
        break;
    }

    return field.thresholdMin || field.thresholdMax || field.thresholdArrow ? (
      threshold.arrow ? (
        iconProps.direction === 'up' ? (
          <ArrowDropUpIcon sx={{ color: iconProps.color, display: 'block', flexShrink: 0, fontSize: 'var(--kpi-icon-font-size)' }} />
        ) : (
          <ArrowDropDownIcon sx={{ color: iconProps.color, display: 'block', flexShrink: 0, fontSize: 'var(--kpi-icon-font-size)' }} />
        )
      ) : iconProps.good ? (
        <DoneIcon sx={{ color: iconProps.color, display: 'block', flexShrink: 0, fontSize: 'var(--kpi-icon-font-size)', ml: 1 }} />
      ) : (
        <ClearIcon sx={{ color: iconProps.color, display: 'block', flexShrink: 0, fontSize: 'var(--kpi-icon-font-size)', ml: 1 }} />
      )
    ) : (
      <></>
    );
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      mx={compactLayout ? 1 : 4}
      sx={{ height: `${chartHeight}px` }}
      pt={2}
      mt={1}
    >
      <Paper
        elevation={3}
        sx={{
          width: '100%',
          height: '80%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'grey.100',
          color: '#607d8b',
          overflow: 'hidden',
        }}
      >
        <Box
          ref={contentRef}
          textAlign="center"
          sx={{
            '--kpi-title-font-size': `${KPI_FONT_LIMITS.title.min}px`,
            '--kpi-value-font-size': `${KPI_FONT_LIMITS.value.min}px`,
            '--kpi-icon-font-size': `${KPI_FONT_LIMITS.icon.min}px`,
            width: '100%',
            height: '100%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
            px: 1,
          }}
        >
          <button
            type="button"
            onClick={handleResetDrill}
            disabled={!hasDrillSelections}
            className={`flex shrink-0 justify-center ${
              hasDrillSelections ? 'cursor-pointer hover:underline' : 'cursor-default'
            }`}
            style={{ maxWidth: '100%', minWidth: 0, padding: 0, border: 0, background: 'transparent' }}
          >
            <Typography
              variant="h4"
              gutterBottom={!compactLayout}
              sx={{ fontSize: 'var(--kpi-title-font-size)', lineHeight: 1.15, overflowWrap: 'anywhere' }}
            >
              {report.name}
            </Typography>
          </button>
          <DrillSelectionPath filterVals={filterVals} onSelectLevel={handleSelectDrillLevel} />
          <Box
            ref={valueRowRef}
            sx={{
              display: 'flex',
              flexShrink: 0,
              maxWidth: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            <Typography variant="h1" fontWeight="bold" sx={{ fontSize: 'var(--kpi-value-font-size)', lineHeight: 1.05 }}>
              {formattedValue}
            </Typography>
            <KPIIcon value={chartData.data} />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default KPIReport;
